/**
 * Contato e acesso do cliente — o que a barra lateral do Painel edita.
 *
 * **Alterar devolve id novo.** No sistema de hoje, alterar um contato é matar a
 * linha antiga e inserir outra; a API faz igual, para que as duas telas contem a
 * mesma história enquanto convivem. Por isso toda escrita invalida a consulta:
 * não dá para remendar o item na lista, porque o id mudou.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "./cliente"

export type EmailDoContato = {
  id: number
  titulo: string
  email: string
  autorizaEnvio: boolean
  confirmado: boolean
}

export type TelefoneDoContato = {
  id: number
  titulo: string
  ddd: number
  telefone: string
  whatsapp: boolean
  data: string | null
}

export type EnderecoDoContato = {
  id: number
  titulo: string
  endereco: string
  bairro: string
  cidade: string
  estado: string
  pais: string
  cep: string
  codIbge: number
}

export type ContatoDoCliente = {
  clienteId: number
  emails: EmailDoContato[]
  telefones: TelefoneDoContato[]
  enderecos: EnderecoDoContato[]
}

export type DadosDeAcesso = {
  clienteId: number
  login: string
  senha: string
  definido: boolean
  loginSugerido: string
  senhaSugerida: string
}

export type CookieDoCliente = {
  id: number
  navegador: string
  data: string
  ip: string
  cookie: string
}

export type AcessoRegistrado = {
  data: string
  navegador: string
  dominio: string
  localizacao: string
  ip: string
  texto: string
}

export type CookiesDoCliente = {
  clienteId: number
  trocasDeMaquina: number
  cookies: CookieDoCliente[]
  ultimosAcessos: AcessoRegistrado[]
}

export type Limpeza = { operacao: string; resultado: string; linhas: number }

/**
 * Os títulos que o sistema de hoje oferece no formulário.
 *
 * A lista é **aberta**: em produção há 12 títulos de telefone em uso, escritos
 * por automações (`ATUALIZADO SUPORTE`, `ATUALIZADO RDSTATION`, `CONFIRMAR`).
 * Uma lista fechada faria o operador trocar o título sem querer ao gravar.
 */
export const TITULOS_DE_EMAIL = ["Principal", "Casa", "Trabalho", "Outros"]
export const TITULOS_DE_TELEFONE = [
  "Principal", "Casa", "Celular", "Trabalho", "Confirmado por SMS", "Outros",
]
export const TITULOS_DE_ENDERECO = ["Principal", "Casa", "Trabalho", "Outros"]

export const UFS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT",
  "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO",
]

const raiz = (clienteId: number) => `api/clientes/${clienteId}`

// ------------------------------------------------------------------ contato

export function usarContatoDoCliente(clienteId: number | null) {
  return useQuery({
    queryKey: ["contato-do-cliente", clienteId],
    enabled: clienteId != null,
    queryFn: async () => {
      const r = await api.get<ContatoDoCliente>(`${raiz(clienteId!)}/contato`)
      return r.data
    },
  })
}

type Alvo = "emails" | "telefones" | "enderecos"

/**
 * Grava um contato. Sem `id` inclui; com `id` altera — e o que volta tem **id
 * novo**, porque alterar é matar e inserir.
 */
export function usarSalvarContato(clienteId: number, alvo: Alvo) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id?: number; dados: Record<string, unknown> }) => {
      const rota = `${raiz(clienteId)}/contato/${alvo}`
      const r = id
        ? await api.put(`${rota}/${id}`, dados)
        : await api.post(rota, dados)
      return r.data
    },
    onSuccess: () => {
      fila.invalidateQueries({ queryKey: ["contato-do-cliente", clienteId] })
      // A ficha traz a lista de e-mails junto; sem isto ela fica mostrando o antigo.
      fila.invalidateQueries({ queryKey: ["cliente", clienteId] })
    },
  })
}

export function usarExcluirContato(clienteId: number, alvo: Alvo) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) => {
      // O motivo vai no corpo do DELETE — é como as outras operações de cliente
      // já fazem (o desbloquear manda `data` no axios).
      await api.delete(`${raiz(clienteId)}/contato/${alvo}/${id}`, { data: { motivo } })
    },
    onSuccess: () => {
      fila.invalidateQueries({ queryKey: ["contato-do-cliente", clienteId] })
      fila.invalidateQueries({ queryKey: ["cliente", clienteId] })
    },
  })
}

export function usarConfirmarWhatsapp(clienteId: number) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.post(`${raiz(clienteId)}/contato/telefones/${id}/whatsapp`)
    },
    onSuccess: () => fila.invalidateQueries({ queryKey: ["contato-do-cliente", clienteId] }),
  })
}

// ------------------------------------------------------------- dados de acesso

export function usarDadosDeAcesso(clienteId: number | null, ligado: boolean) {
  return useQuery({
    queryKey: ["acesso-do-cliente", clienteId],
    enabled: clienteId != null && ligado,
    queryFn: async () => {
      const r = await api.get<DadosDeAcesso>(`${raiz(clienteId!)}/acesso`)
      return r.data
    },
  })
}

export function usarSalvarDadosDeAcesso(clienteId: number) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { login: string; senha: string; motivo: string }) => {
      const r = await api.put<DadosDeAcesso>(`${raiz(clienteId)}/acesso`, dados)
      return r.data
    },
    onSuccess: () => fila.invalidateQueries({ queryKey: ["acesso-do-cliente", clienteId] }),
  })
}

export function usarEnviarDadosDeAcesso(clienteId: number) {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<Limpeza>(`${raiz(clienteId)}/acesso/enviar`)
      return r.data
    },
  })
}

// ------------------------------------------------------------------- cookies

export function usarCookiesDoCliente(clienteId: number | null, ligado: boolean) {
  return useQuery({
    queryKey: ["cookies-do-cliente", clienteId],
    enabled: clienteId != null && ligado,
    queryFn: async () => {
      const r = await api.get<CookiesDoCliente>(`${raiz(clienteId!)}/acesso/cookies`)
      return r.data
    },
  })
}

export function usarLimparAcesso(clienteId: number) {
  const fila = useQueryClient()
  return useMutation({
    mutationFn: async ({ o_que, motivo }: { o_que: "trocas" | "pc" | "movel"; motivo: string }) => {
      const rota = o_que === "trocas"
        ? `${raiz(clienteId)}/acesso/trocas-de-maquina`
        : `${raiz(clienteId)}/acesso/cookies/${o_que}`
      const r = await api.delete<Limpeza>(rota, { data: { motivo } })
      return r.data
    },
    onSuccess: () => fila.invalidateQueries({ queryKey: ["cookies-do-cliente", clienteId] }),
  })
}
